import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { compose, patch, removeItem } from '@ngxs/store/operators';
import {
  JobControllerService,
  SystemButtonControllerService,
} from '@osos/shared/api';
import {
  ButtonActionTypeEnum,
  LoadingIndicator,
  LocalizedValueModel,
  ManagedHttpContext,
  SystemButtonActionWithOptions,
  SystemButtonDataPresenterModel,
  SystemButtonStateForEntriesPresenterModel,
} from '@osos/shared/types';
import cloneDeep from 'lodash-es/cloneDeep';
import { interval, Observable, of, Subscription } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  DataEntityActionFactoryService,
  DataViewsFactoryService,
  EntityFactoryService,
} from '../../factories';
import { SystemButtonDataFactoryService } from '../../factories/system-button-data-factory/system-button-data-factory.service';
import { LoadingActions } from '../loading/loading.actions';
import { CoreSystemButtonsActions } from './core-system-button.action';

export interface CoreSystemButtonsStateModel {
  presentedButtons?: { [key: string]: SystemButtonDataPresenterModel[] };
  transitoryButtonAction?: {
    [key: string]: SystemButtonActionWithOptions;
  };
  loading?: LoadingIndicator;
  currentExecutionButtons?: CurrentExecutionButtons;
  currentMessageId?: string;
  buttonsStateForEntries?: SystemButtonStateForEntriesPresenterModel[];
}
export interface CurrentExecutionButtons {
  [key: string]: {
    messageId: string;
    buttonId: string;
    message?: LocalizedValueModel;
    status: JobStatusEnum;
  };
}
export enum JobStatusEnum {
  InProgress = 'InProgress',
  Success = 'Success',
  Failed = 'Failed',
}
@State<CoreSystemButtonsStateModel>({
  name: 'coreSystemButtonss',
  defaults: {
    presentedButtons: {},
    transitoryButtonAction: {},
    loading: new LoadingIndicator(),
    currentExecutionButtons: {},
    currentMessageId: undefined,
    buttonsStateForEntries: [],
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class CoreSystemButtonsState {
  constructor(
    private buttonWidgetService: SystemButtonControllerService,
    private buttonFactoryService: SystemButtonDataFactoryService,
    private JobsService: JobControllerService,
    private dataEntityActionFactoryService: DataEntityActionFactoryService,
    private dataEntityFactoryService: EntityFactoryService,
    private dataViewsFactoryService: DataViewsFactoryService,
  ) {}
  private _jobStatusSubscriptions: { [key: string]: Subscription } = {};
  private _refreshJobStatusInterval = 2000; // 2 seconds
  private static _getKey(triggerId: string, buttonId: string): string {
    return `${triggerId ?? ''}${buttonId}`;
  }
  // --------------------------------- SELECTORS ------------------------------

  /**
   * select transitory modules.
   * it's valid to be readed once after trigger TransitoryFetchModules action
   */
  static presentedButtons(
    entityId: string,
  ): (state: CoreSystemButtonsStateModel) => SystemButtonDataPresenterModel[] {
    return createSelector(
      [CoreSystemButtonsState],
      (state: CoreSystemButtonsStateModel) =>
        state.presentedButtons[entityId] ?? [],
    );
  }

  static transitoryButtonAction(
    buttonId: string,
    triggerId: string,
  ): (state: CoreSystemButtonsStateModel) => SystemButtonActionWithOptions {
    const key = this._getKey(triggerId, buttonId);
    return createSelector(
      [CoreSystemButtonsState],
      (state: CoreSystemButtonsStateModel) => state.transitoryButtonAction[key],
    );
  }

  @Selector([CoreSystemButtonsState])
  static isFetching(state: CoreSystemButtonsStateModel): boolean {
    return state.loading.fetch;
  }

  @Selector([CoreSystemButtonsState])
  static currentExecutionButtons(
    state: CoreSystemButtonsStateModel,
  ): CurrentExecutionButtons {
    return state.currentExecutionButtons;
  }

  @Selector([CoreSystemButtonsState])
  static currentSystemButtonsStateForEntries(
    state: CoreSystemButtonsStateModel,
  ): SystemButtonStateForEntriesPresenterModel[] {
    return state.buttonsStateForEntries;
  }

  @Selector([CoreSystemButtonsState])
  static currentMessageId(
    state: CoreSystemButtonsStateModel,
  ): string | undefined {
    return state.currentMessageId;
  }

  // --------------------------------- ACTIONS --------------------------------

  @Action(CoreSystemButtonsActions.FetchSystemButtons)
  fetchSystemButtons(
    { setState }: StateContext<CoreSystemButtonsStateModel>,
    { entityId, viewId }: CoreSystemButtonsActions.FetchSystemButtons,
  ): Observable<SystemButtonDataPresenterModel[]> {
    setState(
      patch<CoreSystemButtonsStateModel>({
        presentedButtons: patch({
          [entityId]: [],
        }),
        loading: patch<LoadingIndicator>({
          fetch: true,
        }),
      }),
    );
    return this.buttonWidgetService
      .findForUser({
        relatedToId: entityId,
        context: viewId
          ? new ManagedHttpContext({ publicAccessId: viewId })
          : undefined,
      })
      .pipe(
        map((buttons) => {
          return buttons.map((button) =>
            this.buttonFactoryService.createPresenterModel(button),
          );
        }),
        tap((buttons: SystemButtonDataPresenterModel[]) => {
          setState(
            patch<CoreSystemButtonsStateModel>({
              presentedButtons: patch({
                [entityId]: buttons,
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<CoreSystemButtonsStateModel>({
              loading: patch<LoadingIndicator>({
                fetch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(CoreSystemButtonsActions.FetchSystemButtonsStateForEntries, {
    cancelUncompleted: true,
  })
  fetchSystemButtonsStateForEntries(
    { setState, getState }: StateContext<CoreSystemButtonsStateModel>,
    {
      entityId,
      viewId,
      entriesIds,
      selectAll,
    }: CoreSystemButtonsActions.FetchSystemButtonsStateForEntries,
  ): Observable<SystemButtonStateForEntriesPresenterModel[]> {
    const entityButtons = getState().presentedButtons[entityId];
    if (!entityButtons?.length) {
      return of(null);
    }
    return this.buttonWidgetService
      .getSystemButtonsStatusForEntries({
        relatedToId: entityId,
        body: {
          dataEntriesIds: entriesIds,
          selectAll: selectAll,
        },
        context: viewId
          ? new ManagedHttpContext({ publicAccessId: viewId })
          : null,
      })
      .pipe(
        tap(
          (
            buttonsStateForEntries: SystemButtonStateForEntriesPresenterModel[],
          ) => {
            setState(
              patch<CoreSystemButtonsStateModel>({
                buttonsStateForEntries: buttonsStateForEntries,
              }),
            );
          },
        ),
      );
  }

  @Action(CoreSystemButtonsActions.FetchSystemButtonJobStatus, {
    cancelUncompleted: true,
  })
  fetchSystemButtonsJobStatus(
    { setState, getState }: StateContext<CoreSystemButtonsStateModel>,
    { messageId }: CoreSystemButtonsActions.FetchSystemButtonJobStatus,
  ) {
    return this.JobsService.getJobStatusByMessageId({
      messageId,
    }).pipe(
      tap((response) => {
        const foundButtonState = Object.values(
          getState().currentExecutionButtons,
        )?.find((b) => b.messageId === messageId);
        setState(
          patch<CoreSystemButtonsStateModel>({
            currentExecutionButtons: patch({
              [messageId]: {
                messageId,
                status: response.status as any,
                buttonId: foundButtonState.buttonId,
                message: response.message,
              },
            }),
          }),
        );

        if (response.status !== JobStatusEnum.InProgress) {
          this._jobStatusSubscriptions[messageId]?.unsubscribe();
          delete this._jobStatusSubscriptions[messageId];
          const clonedExecutionStatus = cloneDeep(
            getState().currentExecutionButtons,
          );
          delete clonedExecutionStatus[messageId];
          setState(
            patch<CoreSystemButtonsStateModel>({
              currentExecutionButtons: clonedExecutionStatus,
            }),
          );
        }
      }),
    );
  }

  @Action(CoreSystemButtonsActions.SubscribeToSystemButtonJobStatus)
  subscribeToSystemButtonJobStatusFetching(
    { dispatch, getState, setState }: StateContext<CoreSystemButtonsStateModel>,
    { messageId }: CoreSystemButtonsActions.SubscribeToSystemButtonJobStatus,
  ) {
    const foundButtonState = getState().currentExecutionButtons[messageId];
    if (!foundButtonState) {
      return;
    }
    // Schedule the button job status fetching
    this._jobStatusSubscriptions[messageId] = interval(
      this._refreshJobStatusInterval,
    )
      .pipe(
        switchMap(() =>
          dispatch(
            new CoreSystemButtonsActions.FetchSystemButtonJobStatus(
              foundButtonState?.messageId,
            ),
          ),
        ),
      )
      .subscribe({
        error: () => {
          this._jobStatusSubscriptions[messageId]?.unsubscribe();
          delete this._jobStatusSubscriptions[messageId];
          const currentExecutionButtons = cloneDeep(
            getState().currentExecutionButtons,
          );
          delete currentExecutionButtons[messageId];
          setState(
            patch<CoreSystemButtonsStateModel>({
              currentExecutionButtons: currentExecutionButtons,
            }),
          );
        },
      });
  }
  @Action(CoreSystemButtonsActions.ExecuteSystemButton, {
    cancelUncompleted: true,
  })
  executeSystemButton(
    { setState, dispatch }: StateContext<CoreSystemButtonsStateModel>,
    { payload }: CoreSystemButtonsActions.ExecuteSystemButton,
  ): Observable<any> {
    if (!payload.hideLoading) {
      dispatch(new LoadingActions.SetCommandLoading(true));

      setState(
        patch<CoreSystemButtonsStateModel>({
          loading: patch<LoadingIndicator>({
            fetch: true,
          }),
        }),
      );
    }

    const selectAllEnabled = !!payload.selectAll?.enabled;
    if (
      payload.mode === ButtonActionTypeEnum.MultipleEntries &&
      selectAllEnabled
    ) {
      payload.source = [];
      payload.selectAll = payload.selectAll;
    }
    return this.buttonWidgetService
      .executeSystemButton({
        id: payload.buttonId,
        body: {
          mode: payload.mode,
          source: payload.source,
          // overrideValues: payload.overrideValues,
          askUserValue: payload.askUserValue,
          selectAll: payload.selectAll ?? null,
        },
        context: payload.viewId
          ? new ManagedHttpContext({ publicAccessId: payload.viewId })
          : undefined,
      })
      .pipe(
        tap((messageId) => {
          setState(
            patch<CoreSystemButtonsStateModel>({
              currentExecutionButtons: patch({
                [messageId]: {
                  messageId,
                  status: JobStatusEnum.InProgress,
                  buttonId: payload.buttonId,
                },
              }),
              currentMessageId: messageId,
            }),
          );
        }),
        finalize(() => {
          if (!payload.hideLoading) {
            dispatch(new LoadingActions.SetCommandLoading(false));

            setState(
              patch<CoreSystemButtonsStateModel>({
                loading: patch<LoadingIndicator>({
                  fetch: false,
                }),
              }),
            );
          }
        }),
      );
  }

  @Action(CoreSystemButtonsActions.SetSystemButtonsInformativeLoader, {
    cancelUncompleted: true,
  })
  setSystemButtonsInformativeLoader(
    { dispatch }: StateContext<CoreSystemButtonsStateModel>,
    { isLoading }: CoreSystemButtonsActions.SetSystemButtonsInformativeLoader,
  ) {
    dispatch(new LoadingActions.SetCommandLoading(isLoading));
  }

  @Action(CoreSystemButtonsActions.DeleteSystemButton)
  deleteButton(
    { setState, getState }: StateContext<CoreSystemButtonsStateModel>,
    { buttonId, entityId }: CoreSystemButtonsActions.DeleteSystemButton,
  ) {
    const entityButtons = getState().presentedButtons[entityId];
    if (entityButtons) {
      setState(
        patch<CoreSystemButtonsStateModel>({
          presentedButtons: patch<{
            [key: string]: SystemButtonDataPresenterModel[];
          }>({
            [entityId]: removeItem((x) => x.id == buttonId),
          }),
        }),
      );
    }
  }

  @Action(CoreSystemButtonsActions.FetchSystemButtonAction, {
    cancelUncompleted: true,
  })
  fetchSystemButtonAction(
    { setState, getState }: StateContext<CoreSystemButtonsStateModel>,
    {
      buttonId,
      triggerId,
      cached,
    }: CoreSystemButtonsActions.FetchSystemButtonAction,
  ): Observable<any> {
    const buttonActionKey = CoreSystemButtonsState._getKey(triggerId, buttonId);
    const _buttonActions = getState().transitoryButtonAction[buttonActionKey];
    if (cached && !!_buttonActions) {
      return of(_buttonActions);
    }
    setState(
      patch<CoreSystemButtonsStateModel>({
        transitoryButtonAction: patch({
          [buttonActionKey]: undefined,
        }),
        loading: patch<LoadingIndicator>({
          fetch: true,
        }),
      }),
    );
    return this.buttonWidgetService
      .getSystemButtonActionWithOptionsV2({
        id: buttonId,
      })
      .pipe(
        tap(({ actions, askUserEntity, askUserView }) => {
          const buttonPresenterActions = actions.map((buttonAction) =>
            this.dataEntityActionFactoryService.createPresenterModel(
              buttonAction,
            ),
          );

          const askUserPresenterEntity = askUserEntity
            ? this.dataEntityFactoryService.createPresenterModel(askUserEntity)
            : null;
          const askUserPresenterView = askUserView
            ? this.dataViewsFactoryService.createPresenterModel(askUserView)
            : null;
          setState(
            patch<CoreSystemButtonsStateModel>({
              transitoryButtonAction: patch({
                [buttonActionKey]: {
                  actions: buttonPresenterActions,
                  askUserEntity: askUserPresenterEntity,
                  askUserView: askUserPresenterView,
                },
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<CoreSystemButtonsStateModel>({
              loading: patch<LoadingIndicator>({
                fetch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(CoreSystemButtonsActions.ClearSystemButtonActionOptions)
  clearSystemButtonActionOptions(
    { setState, getState }: StateContext<CoreSystemButtonsStateModel>,
    { triggerIds }: CoreSystemButtonsActions.ClearSystemButtonActionOptions,
  ) {
    const _cashedButtonActions = getState().transitoryButtonAction;
    const itemsToRemove = Object.keys(_cashedButtonActions).filter((key) =>
      triggerIds.some((triggerId) => key.includes(triggerId)),
    );
    const composedOperators = itemsToRemove.map((key) =>
      patch<{ [key: string]: SystemButtonActionWithOptions }>({
        [key]: undefined,
      }),
    );
    setState(
      patch<CoreSystemButtonsStateModel>({
        transitoryButtonAction: compose(...composedOperators),
      }),
    );
  }
}
